import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { Link, type LinkProps } from 'react-router-dom'
import { BUTTON_INTERACTIVE_CLASSES } from '@proaxiv/ui'

/**
 * Visual style variants available for the Button component. `"unstyled"` applies no
 * colors/layout/size presets — combine with `className` for full control while still getting
 * the centralized interactive behavior (hand cursor, disabled state).
 */
export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'success' | 'unstyled'

/** Size presets available for the Button component. Ignored when `variant` is `"unstyled"`. */
export type ButtonSize = 'sm' | 'md' | 'lg'

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  leftIcon?: ReactNode
  fullWidth?: boolean
}

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary: 'bg-pax-blue text-white hover:bg-pax-blue/90 border border-transparent',
  // Hovers on `pax-purple` (a saturated brand color) rather than `pax-light-purple` — the latter
  // is the page/card background color itself under the TMS brand (both are pure white there), so
  // hovering it produced no visible change at all. `pax-purple` at low opacity stays a subtle
  // lavender tint for PROAXIV and becomes a visible light-gray tint for TMS, so the hover always
  // "lights up" regardless of active brand.
  secondary: 'bg-transparent text-gray-700 border border-pax-purple/40 hover:bg-pax-purple/15',
  danger: 'bg-transparent text-pax-red border border-pax-red/30 hover:bg-pax-red/5',
  ghost: 'bg-transparent text-pax-blue border border-transparent hover:bg-pax-blue/8',
  success: 'bg-pax-green text-white hover:bg-pax-green/90 border border-transparent',
  unstyled: '',
}

const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: 'px-3 py-1 text-xs rounded-lg  gap-1.5',
  md: 'px-5 py-2 text-sm rounded-xl  gap-2',
  lg: 'px-6 py-3 text-base rounded-xl gap-2',
}

/** Shared class-name builder used by both Button and ButtonLink. */
function buttonClasses(
  variant: ButtonVariant = 'primary',
  size: ButtonSize = 'md',
  fullWidth = false,
  className = ''
): string {
  const isUnstyled = variant === 'unstyled'
  return [
    BUTTON_INTERACTIVE_CLASSES,
    !isUnstyled &&
      'inline-flex items-center justify-center font-medium transition-all duration-150 focus-visible:ring-pax-blue/50 focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:outline-none disabled:opacity-40',
    !isUnstyled && VARIANT_CLASSES[variant],
    !isUnstyled && SIZE_CLASSES[size],
    !isUnstyled && fullWidth ? 'w-full' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')
}

interface ButtonLinkProps extends Omit<LinkProps, 'className'> {
  variant?: ButtonVariant
  size?: ButtonSize
  leftIcon?: ReactNode
  fullWidth?: boolean
  className?: string
  children?: ReactNode
}

/**
 * Renders a React Router `<Link>` styled identically to `<Button>`.
 * Use instead of `<Button onClick={() => navigate(to)}>` so that
 * Ctrl+click / middle-click opens a new tab as users expect.
 */
export function ButtonLink({
  variant = 'primary',
  size = 'md',
  leftIcon,
  fullWidth = false,
  className = '',
  children,
  ...rest
}: ButtonLinkProps) {
  return (
    <Link className={buttonClasses(variant, size, fullWidth, className)} {...rest}>
      {leftIcon && <span className="shrink-0">{leftIcon}</span>}
      {children}
    </Link>
  )
}

/** Reusable PROAXIV-branded button with variant + size presets. */
export function Button({
  variant = 'primary',
  size = 'md',
  leftIcon,
  fullWidth = false,
  className = '',
  children,
  disabled,
  ...rest
}: Props) {
  return (
    <button
      type="button"
      disabled={disabled}
      className={buttonClasses(variant, size, fullWidth, className)}
      {...rest}
    >
      {leftIcon && <span className="shrink-0">{leftIcon}</span>}
      {children}
    </button>
  )
}
